import { YAxisOptions } from "highcharts";

import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { chain } from "@/utils/chain";
import { YAxisAdapter } from "@/utils/chart/adapters/axis/y-axis-adapter";
import { SplineSeriesAdapter } from "@/utils/chart/adapters/series/spline-series-adapter";
import { IAxisAdapter } from "@/utils/chart/adapters/types/common-adapter.type";
import { ITemporalSeriesAdapter } from "@/utils/chart/adapters/types/temporal-chart-adapter.type";

export interface ITemporalChartSpecs<KIND extends string> {
  seriesAdapter: ReadonlyArray<[KIND, ITemporalSeriesAdapter<KIND>]>;
  yAxisAdapter?: ReadonlyArray<[KIND, IAxisAdapter]>;
  accessibilityDescription?: string;
}

export function buildTemporalChartOptions<KIND extends string>(
  specs: ITemporalChartSpecs<KIND>,
  series: TTemporalDataResponseSerie<KIND>[],
): Highcharts.Options {
  const yAxisOpts: YAxisOptions[] = chain(series)
    .uniqBy((serie) => serie.unit)
    .flatMap((serie) => {
      const kind = serie.category;
      const maybeAdapterTuple = specs.yAxisAdapter?.find(([k]) => k === kind);
      const adapter = maybeAdapterTuple?.[1] ?? YAxisAdapter();

      return adapter.adapt(serie);
    })
    .value();

  const seriesOpts = series.flatMap((serie) => {
    const kind = serie.category;
    const maybeAdapterTuple = specs.seriesAdapter.find(([k]) => k === kind);
    const adapter = maybeAdapterTuple ? maybeAdapterTuple[1] : SplineSeriesAdapter();

    return adapter.adapt(serie, yAxisOpts);
  });

  return {
    chart: {
      type: "spline",
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "datetime",
      tickInterval: 24 * 3600 * 1000, // tick every 24h
      crosshair: true,
      labels: {
        format: "{value:%[dm]}",
      },
    },
    yAxis: yAxisOpts,
    tooltip: {
      headerFormat: "{point.key:%[ebY]}<br/>",
    },
    credits: {
      enabled: false,
    },
    series: seriesOpts,
    accessibility: {
      description: specs.accessibilityDescription,
    },
  };
}
